import React from "react";
import {
  Box,
  Drawer,
  IconButton,
  Button,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const toTitleCase = (str: string): string => {
  return str.replace(/_/g, " ").replace(/\w\S*/g, (txt) => {
    return txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase();
  });
};

interface MobileDesignCategoryFilterDrawerProps {
  open: boolean;
  onClose: () => void;
  selectedCategories: string[];
  onCategoryToggle: (category: string) => void;
  onClearAll: () => void;
  onApply: () => void;
  availableCategories: string[];
}

export const MobileDesignCategoryFilterDrawer: React.FC<
  MobileDesignCategoryFilterDrawerProps
> = ({
  open,
  onClose,
  selectedCategories = [],
  onCategoryToggle,
  onApply,
  onClearAll,
  availableCategories = [],
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const handleApply = () => {
    onApply();
    onClose();
  };

  const allSelected =
    availableCategories.length > 0 &&
    availableCategories.every((cat) => selectedCategories.includes(cat));
  const noneSelected = selectedCategories.length === 0;

  const handleSelectAll = () => {
    if (allSelected) {
      onClearAll();
    } else {
      availableCategories.forEach((category) => {
        if (!selectedCategories.includes(category)) {
          onCategoryToggle(category);
        }
      });
    }
  };

  const handleClear = () => {
    onClearAll();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: isMobile ? "100%" : "400px", maxWidth: "100%" },
      }}
    >
      <Box sx={{ p: 2 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6">Filter Designs</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <FormGroup>
          {availableCategories.map((category) => (
            <FormControlLabel
              key={category}
              control={
                <Checkbox
                  checked={selectedCategories.includes(category)}
                  onChange={() => onCategoryToggle(category)}
                />
              }
              label={toTitleCase(category)}
            />
          ))}
        </FormGroup>

        <Box mt={3} display="flex" flexDirection="column" gap={2}>
          <Box display="flex" gap={2}>
            <Button
              variant="outlined"
              onClick={handleSelectAll}
              disabled={allSelected}
              fullWidth
              sx={{
                textTransform: "none",
                color: allSelected ? "rgba(0, 0, 0, 0.26)" : "primary.main",
                borderColor: allSelected
                  ? "rgba(0, 0, 0, 0.12)"
                  : "primary.main",
                "&:hover": {
                  borderColor: allSelected
                    ? "rgba(0, 0, 0, 0.12)"
                    : "primary.dark",
                },
              }}
            >
              Select All
            </Button>
            <Button
              variant="outlined"
              onClick={handleClear}
              disabled={noneSelected}
              fullWidth
            >
              Clear All
            </Button>
          </Box>
          <Button
            variant="contained"
            onClick={handleApply}
            fullWidth
            sx={{
              textTransform: "none",
              padding: "8px 16px",
              fontSize: "1rem",
            }}
          >
            Apply
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};
